/**
 * DealRail.tsx — the deal page's right rail (deal detail slice 3, §39.9 order
 * 3; spec §3.5 C, D; People moved to DealPeopleCard.tsx in slice 8): Forecast (the category pill and
 * the Override link per R4, probability with its source, the weighted value,
 * the plain-language box) and Details (44px rows, click a value to edit it
 * inline — each save is ONE field through the existing PUT diff; R8's Notes
 * row, so the standalone Notes card is retired with nothing lost).
 *
 * Every row edits through the SAME helpers the old Edit-mode card used
 * (draftFromDeal → dealEditDiff), so a row emits only its own field, the
 * clear-to-inherit / clear-to-uncategorised rules hold, and a field frozen on
 * a closed deal (fieldEditable) is never offered as an input — it shows a
 * lock and the reason. The server's named 409 stays the backstop.
 */
import { useEffect, useRef, useState } from 'react'
import { Lock, Pencil, Link2, Building2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import type { DealDetail } from '../../types'
import type { SalesFieldHistoryEntry } from '../../../../api/types'
import {
  useAsync, updateDeal, updateDealNotes, linkDealAccount, upsertForecastOverride, deleteForecastOverride,
  getIssuedInvoice,
} from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { CurrencySelect } from '../../CurrencySelect'
import { OwnerSelect } from './OwnerSelect'
import { AccountPicker, type AccountPick } from './AccountPicker'
import { useWriteAccess } from '../../../../lib/writeAccess'
import {
  lostReasonLabel, sourceLabel, SOURCE_OPTIONS, PRIORITY_OPTIONS, FORECAST_CATEGORY_PICKER, FORECAST_CATEGORY_OPTIONS,
  forecastCategoryLabel,
} from './vocab'
import { gapChip } from './forecastView'
import { draftFromDeal, dealEditDiff, fieldEditable, isClosedStage, probabilityInputValid, valueInputValid, FROZEN_REASON, type DealEditDraft, type DealEditField } from './dealEdit'
import { formatDay, weightedLabel, notWeightedReason, probabilityLine, forecastExplanation } from './dealRail'
import { actorName } from './dealTimeline'
import { accent, warning } from '../../../../theme/tokens'
import { money } from '../../../../lib/money'

/** Spec §2.3 status pill: 4×10 padding, pill radius, 12px/500, sentence case. */
const pillCls = 'inline-flex items-center rounded-full px-2.5 py-1 text-12 font-medium bg-surface-control text-ink-primary'
const inputCls = 'h-9 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent'

/** One rail row (spec §3.5 D): 44px, a 13px muted label, a 14px/500 value. */
export function RailRow({ label, children, align = 'center' }: { label: string; children: React.ReactNode; align?: 'center' | 'start' }) {
  return (
    <div className={`flex ${align === 'start' ? 'items-start py-3' : 'items-center py-2'} justify-between gap-4 min-h-[44px] border-b border-edge-row last:border-0`}>
      <div className="text-13 text-ink-muted flex-shrink-0">{label}</div>
      <div className="text-14 font-medium text-ink-primary text-right min-w-0">{children}</div>
    </div>
  )
}

/** A frozen value on a closed deal: the value, a lock, the reason on hover. */
function Frozen({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1 text-ink-secondary" title={FROZEN_REASON}><Lock size={11} className="text-ink-faint" aria-hidden="true" />{children}</span>
}

/** The click-to-edit value: a real button carrying the row's label. Every
 *  rail edit is a records-level deal write (lib/writeAccess): without access
 *  the value renders as plain text. */
function EditValue({ label, onEdit, children, style }: { label: string; onEdit: () => void; children: React.ReactNode; style?: React.CSSProperties }) {
  const { records } = useWriteAccess()
  if (!records) return <span className="text-right" style={style}>{children}</span>
  return (
    <button type="button" aria-label={`Edit ${label.toLowerCase()}`} onClick={onEdit} style={style}
      className="text-right rounded-sm hover:underline decoration-dotted underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
      {children}
    </button>
  )
}

/** The inline editor's Save / Cancel, and Enter / Esc on its inputs. */
function EditorActions({ saving, disabled, onSave, onCancel }: { saving: boolean; disabled?: boolean; onSave: () => void; onCancel: () => void }) {
  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" className="k-btn-quiet" disabled={saving} onClick={onCancel}>Cancel</button>
      <button type="button" className="k-btn disabled:opacity-60" disabled={saving || disabled} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</button>
    </span>
  )
}
const keys = (save: () => void, cancel: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement)) { e.preventDefault(); save() }
  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel() }
}

type RowKey = 'title' | 'value' | 'expectedClose' | 'owner' | 'source' | 'priority' | 'account' | 'notes' | 'probability' | 'category'

/** One-field saves through the existing diff: the draft is the stored deal
 *  with ONE field changed, so the PUT carries that field only. */
function useFieldSave(deal: DealDetail, onApply: (write: Promise<DealDetail>) => Promise<void>, close: () => void) {
  const [saving, setSaving] = useState(false)
  async function save(patch: Partial<DealEditDraft>, label: string) {
    const body = dealEditDiff(deal, { ...draftFromDeal(deal), ...patch })
    if (Object.keys(body).length === 0) { close(); return }
    setSaving(true)
    try {
      await onApply(updateDeal(deal.id, body))
      close()
      toast.success(`${label} updated`)
    } catch (e) {
      toast.error(apiErrorMessage(e, `Failed to update ${label.toLowerCase()}`))
    } finally {
      setSaving(false)
    }
  }
  return { saving, save }
}

// ── The forecast override row (§19 O-7, kept by R4) ──────────────────────────

function OverrideEditor({ deal, onApply, onDone }: { deal: DealDetail; onApply: (write: Promise<DealDetail>) => Promise<void>; onDone: () => void }) {
  const ov = deal.forecastOverride
  const [category, setCategory] = useState(ov?.category ?? 'commit')
  const [amount, setAmount] = useState(ov?.amountPence != null ? String(ov.amountPence / 100) : '')
  const [saving, setSaving] = useState(false)
  const pence = amount.trim() === '' ? undefined : Math.round(Number(amount) * 100)
  const amountValid = pence === undefined || (Number.isFinite(pence) && pence >= 0)
  async function save() {
    if (!amountValid) return
    setSaving(true)
    try {
      await onApply(upsertForecastOverride(deal.id, { category, ...(pence === undefined ? {} : { amountPence: pence }) }))
      onDone()
      toast.success(ov ? 'Override updated' : 'Override set')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to set the override'))
    } finally { setSaving(false) }
  }
  async function remove() {
    setSaving(true)
    try {
      await onApply(deleteForecastOverride(deal.id))
      onDone()
      toast.success('Override removed — the deal\'s own category stands')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to remove the override'))
    } finally { setSaving(false) }
  }
  return (
    <div className="py-3 border-b border-edge-row last:border-0" data-override-editor onKeyDown={keys(() => void save(), onDone)}>
      <div className="text-13 text-ink-muted mb-2">Forecast override</div>
      <div className="flex flex-wrap items-center gap-2">
        {/* The four slugs only — "no override" is the Remove button. */}
        <select value={category} onChange={e => setCategory(e.target.value)} className={`${inputCls} bg-surface-card`} aria-label="Override category">
          {FORECAST_CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input type="number" min="0" step="0.01" inputMode="decimal" value={amount} placeholder="Amount (optional)"
          onChange={e => setAmount(e.target.value)} aria-invalid={!amountValid || undefined}
          className={`${inputCls} w-36`} aria-label="Override amount" />
        <button type="button" className="k-btn disabled:opacity-60" disabled={saving || !amountValid} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
        {ov && <button type="button" className="k-btn-secondary" disabled={saving} onClick={() => void remove()}>Remove</button>}
        <button type="button" className="k-btn-quiet" disabled={saving} onClick={onDone}>Cancel</button>
      </div>
      <div className="text-12 text-ink-muted mt-2">Blank amount = the deal's own value stands; the category is required. The deal's own category is never changed by an override.</div>
    </div>
  )
}

// ── Forecast (spec §3.5 C) ───────────────────────────────────────────────────

export function ForecastCard({ deal, history, onApply }: { deal: DealDetail; history: readonly SalesFieldHistoryEntry[]; onApply: (write: Promise<DealDetail>) => Promise<void> }) {
  const [editing, setEditing] = useState<'category' | 'probability' | 'override' | null>(null)
  const [cat, setCat] = useState('')
  const [prob, setProb] = useState('')
  const close = () => setEditing(null)
  const { saving, save } = useFieldSave(deal, onApply, close)
  const ov = deal.forecastOverride
  const closed = isClosedStage(deal.stage)
  const catEditable = fieldEditable('forecastCategory', deal.stage)
  const probEditable = fieldEditable('probability', deal.stage)
  const weighted = weightedLabel(deal)
  const line = probabilityLine(deal.probabilitySource, deal.stage)
  const pill = ov ? `${forecastCategoryLabel(ov.category)} · overridden` : forecastCategoryLabel(deal.forecastCategory)
  // Category, probability and the override are records-level deal writes
  // (lib/writeAccess): without access the card reads only.
  const { records } = useWriteAccess()

  return (
    <div className="k-card" data-rail="forecast">
      <div className="k-card-head"><div className="k-card-title">Forecast</div></div>
      <div className="px-5 py-2">
        {/* Category: the pill (click = the deal's own category) and, per R4, the
            Override link ONLY when no override is set. */}
        {editing === 'category' ? (
          <RailRow label="Category">
            <span className="inline-flex flex-wrap justify-end items-center gap-2" onKeyDown={keys(() => void save({ forecastCategory: cat }, 'Forecast category'), close)}>
              <select value={cat} onChange={e => setCat(e.target.value)} className={`${inputCls} bg-surface-card`} aria-label="The deal's own forecast category" autoFocus>
                {FORECAST_CATEGORY_PICKER.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <EditorActions saving={saving} onSave={() => void save({ forecastCategory: cat }, 'Forecast category')} onCancel={close} />
            </span>
          </RailRow>
        ) : (
          <RailRow label="Category">
            <span className="inline-flex items-center gap-3">
              {catEditable && !records ? <span className={pillCls} data-forecast-pill>{pill}</span> : catEditable ? (
                <button type="button" aria-label="Edit the deal's own forecast category" className={`${pillCls} hover:opacity-80`} data-forecast-pill
                  onClick={() => { setCat(deal.forecastCategory ?? ''); setEditing('category') }}>{pill}</button>
              ) : <span className={pillCls} data-forecast-pill><Frozen>{pill}</Frozen></span>}
              {records && !ov && !closed && (
                <button type="button" className="text-13 font-medium hover:underline" style={{ color: accent.DEFAULT }}
                  onClick={() => setEditing('override')}>Override</button>
              )}
            </span>
          </RailRow>
        )}
        {/* R4: with an override set, O-7's row stays — category · amount, the
            gap chip, Change. */}
        {editing === 'override' ? (
          <OverrideEditor deal={deal} onApply={onApply} onDone={close} />
        ) : ov ? (
          <RailRow label="Override" align="start">
            <span className="inline-flex flex-col items-end gap-0.5 max-w-60" data-override={ov.category}>
              <span>{forecastCategoryLabel(ov.category)}{ov.amountPence !== null ? ` · ${money(ov.amountPence / 100, deal.currency)}` : ''}</span>
              <span className="text-12 text-ink-secondary font-normal whitespace-normal" data-gap-chip>
                {gapChip(deal.forecastCategory ?? '', ov.category, deal.forecastGapPence ?? null, ov.amountPence !== null)}
              </span>
              {records && !closed && (
                <button type="button" className="k-btn-quiet inline-flex items-center gap-1" aria-label="Change the override" onClick={() => setEditing('override')}>
                  <Pencil size={12} aria-hidden="true" /> Change
                </button>
              )}
            </span>
          </RailRow>
        ) : null}

        {/* Probability: "{n}%", then where it comes from and the verb. */}
        {editing === 'probability' ? (
          <RailRow label="Probability" align="start">
            <span className="inline-flex flex-col items-end gap-1" onKeyDown={keys(() => void save({ probability: prob }, 'Probability'), close)}>
              <span className="inline-flex items-center gap-1">
                <input type="number" min={0} max={100} step={1} inputMode="numeric" value={prob} autoFocus
                  placeholder={deal.effectiveProbability != null ? String(deal.effectiveProbability) : ''}
                  onChange={e => setProb(e.target.value)} aria-invalid={!probabilityInputValid(prob) || undefined}
                  className={`${inputCls} w-20 text-right`} aria-label="Probability" />
                <span className="text-13 text-ink-muted">%</span>
              </span>
              <span className="text-12 text-ink-muted font-normal">Blank goes back to the stage default</span>
              <EditorActions saving={saving} disabled={!probabilityInputValid(prob)} onSave={() => void save({ probability: prob }, 'Probability')} onCancel={close} />
            </span>
          </RailRow>
        ) : (
          <RailRow label="Probability" align="start">
            {deal.effectiveProbability != null ? (
              <span className="inline-flex flex-col items-end gap-0.5">
                {probEditable ? <span>{deal.effectiveProbability}%</span> : <Frozen>{deal.effectiveProbability}%</Frozen>}
                <span className="text-12 text-ink-muted font-normal">
                  {line.source}
                  {probEditable && records && <> · <button type="button" className="hover:underline font-medium" style={{ color: accent.DEFAULT }}
                    onClick={() => { setProb(deal.probability == null ? '' : String(deal.probability)); setEditing('probability') }}>{line.action}</button></>}
                </span>
              </span>
            ) : <span className="text-ink-muted font-normal">Not assessed</span>}
          </RailRow>
        )}

        <RailRow label="Weighted value">
          {/* Slice 8: the server's figure (row 216), "under £1" when it would
              print £0 on a deal with value (row 217); the exact GBP figure is
              the tooltip. */}
          {weighted != null && deal.weightedPence != null
            ? <span title={money(deal.weightedPence / 100, 'GBP')} data-rail-weighted>{weighted}</span>
            : <span className="text-ink-muted font-normal">{notWeightedReason(deal)}</span>}
        </RailRow>

        {/* ✦ The explanation box (spec §3.5 C), in plain words. */}
        <div className="my-3 rounded-[12px] bg-surface-panel px-4 py-3 text-13 text-ink-primary flex gap-2" data-forecast-explanation>
          <Sparkles size={14} className="text-ink-faint flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>{forecastExplanation(deal, history)}</span>
        </div>
      </div>
    </div>
  )
}

// ── Details (spec §3.5 D; R8's Notes row) ────────────────────────────────────

function InvoiceRow({ invoiceId, onNavigate }: { invoiceId: string; onNavigate?: (dest: string) => void }) {
  const { data, loading, error } = useAsync(() => getIssuedInvoice(invoiceId), [invoiceId])
  return (
    <RailRow label="Invoice">
      {loading ? 'Loading…' : error || !data ? 'Unavailable' : onNavigate ? (
        <button type="button" onClick={() => onNavigate('finance.issued-invoices')} title="View in Issued Invoices"
          className="font-medium hover:underline" style={{ color: accent.DEFAULT }}>{data.invoiceNumber}</button>
      ) : data.invoiceNumber}
    </RailRow>
  )
}

export function DetailsCard({ deal, history, onApply, onOpenAccount, onNavigate, editTitleSignal }: {
  deal: DealDetail
  history: readonly SalesFieldHistoryEntry[]
  onApply: (write: Promise<DealDetail>) => Promise<void>
  onOpenAccount?: (accountId: string) => void
  onNavigate?: (dest: string) => void
  /** Bumped by the ⋯ menu's Edit: opens the Title row's editor. */
  editTitleSignal: number
}) {
  const [editing, setEditing] = useState<RowKey | null>(null)
  const [draft, setDraft] = useState<DealEditDraft>(() => draftFromDeal(deal))
  const [notes, setNotes] = useState('')
  const [notesSaving, setNotesSaving] = useState(false)
  const [linkPick, setLinkPick] = useState<AccountPick>({ accountId: '', company: '' })
  const close = () => setEditing(null)
  const { saving, save } = useFieldSave(deal, onApply, close)
  const card = useRef<HTMLDivElement>(null)
  // Records access (lib/writeAccess): without it every row reads only and the
  // empty-value invitations ("Add notes", "Assign"…) read as plain states.
  const { records } = useWriteAccess()

  const open = (key: RowKey) => { setDraft(draftFromDeal(deal)); setEditing(key) }
  // The ⋯ menu's Edit: a new signal opens the Title row — adjusted during
  // render (React's "storing information from previous renders"), not in an
  // effect; the effect below only scrolls the card into view.
  const [seenSignal, setSeenSignal] = useState(editTitleSignal)
  if (seenSignal !== editTitleSignal) {
    setSeenSignal(editTitleSignal)
    setDraft(draftFromDeal(deal)); setEditing('title')
  }
  useEffect(() => {
    if (editTitleSignal > 0) card.current?.scrollIntoView?.({ block: 'nearest' })
  }, [editTitleSignal])

  const may = (f: DealEditField) => fieldEditable(f, deal.stage)
  const priorityLabel = PRIORITY_OPTIONS.find(o => o.value === deal.priority)?.label
  const created = history.find(h => h.field === 'created')
  const by = created ? actorName(created.actorUserId, created.actorDisplayName) : 'Unknown user'
  const createdText = deal.createdAt ? `${formatDay(deal.createdAt)}${by !== 'Unknown user' ? ` by ${by}` : ''}` : ''

  async function saveNotes() {
    // An emptied box is a silent no-op on today's partial-update wire (notes
    // cannot be cleared until the clear-to-null slice), so it is never sent.
    if (notes.trim() === '' || notes === deal.notes) { close(); return }
    setNotesSaving(true)
    try {
      await onApply(updateDealNotes(deal.id, notes))
      close()
      toast.success('Notes updated')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update notes'))
    } finally { setNotesSaving(false) }
  }
  async function linkAccount(p: AccountPick) {
    setLinkPick(p)
    if (!p.accountId) return
    try {
      await onApply(linkDealAccount(deal.id, p.accountId, p.company))
      close()
      toast.success(`Linked to ${p.company}`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to link account'))
    }
  }
  const field = (label: string, patch: () => Partial<DealEditDraft>, valid = true) => ({
    onKeyDown: keys(() => { if (valid) void save(patch(), label) }, close),
    actions: <EditorActions saving={saving} disabled={!valid} onSave={() => void save(patch(), label)} onCancel={close} />,
  })

  return (
    <div className="k-card" data-rail="details" ref={card}>
      <div className="k-card-head">
        <div>
          <div className="k-card-title">Details</div>
          {isClosedStage(deal.stage) && <div className="k-card-sub">Closed deal — value, expected close and priority are fixed</div>}
        </div>
      </div>
      <div className="px-5 py-2">
        {/* Title — kept so title editing is not lost (the ⋯ menu's Edit opens it). */}
        <RailRow label="Title">
          {editing === 'title' ? (() => { const f = field('Title', () => ({ title: draft.title }), draft.title.trim() !== ''); return (
            <span className="inline-flex flex-wrap justify-end items-center gap-2" onKeyDown={f.onKeyDown}>
              <input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} className={`${inputCls} w-56`} aria-label="Deal title" autoFocus />
              {f.actions}
            </span>) })() : <EditValue label="Title" onEdit={() => open('title')}>{deal.title}</EditValue>}
        </RailRow>

        <RailRow label="Value">
          {editing === 'value' ? (() => { const f = field('Value', () => ({ value: draft.value, currency: draft.currency }), valueInputValid(draft.value) && draft.value.trim() !== ''); return (
            <span className="inline-flex flex-wrap justify-end items-center gap-2" onKeyDown={f.onKeyDown}>
              <CurrencySelect id="deal-row-currency" value={draft.currency} onChange={c => setDraft({ ...draft, currency: c })} className={`${inputCls} w-24 bg-surface-card`} />
              <input type="number" min="0.01" step="0.01" value={draft.value} onChange={e => setDraft({ ...draft, value: e.target.value })} className={`${inputCls} w-32`} aria-label="Deal value" autoFocus />
              {f.actions}
            </span>) })() : may('value')
            ? <EditValue label="Value" onEdit={() => open('value')}>{money(deal.amountPence / 100, deal.currency)}</EditValue>
            : <Frozen>{money(deal.amountPence / 100, deal.currency)}</Frozen>}
        </RailRow>

        <RailRow label="Expected close">
          {editing === 'expectedClose' ? (() => { const f = field('Expected close', () => ({ expectedClose: draft.expectedClose }), draft.expectedClose !== ''); return (
            <span className="inline-flex flex-wrap justify-end items-center gap-2" onKeyDown={f.onKeyDown}>
              <input type="date" value={draft.expectedClose} onChange={e => setDraft({ ...draft, expectedClose: e.target.value })} className={`${inputCls} w-40`} aria-label="Expected close" autoFocus />
              {f.actions}
            </span>) })() : !may('expectedClose')
            ? <Frozen>{formatDay(deal.expectedClose, 'utc') || 'Not set'}</Frozen>
            : <EditValue label="Expected close" onEdit={() => open('expectedClose')}>{formatDay(deal.expectedClose, 'utc') || <span className="text-ink-muted font-normal">{records ? 'Add a close date' : 'Not set'}</span>}</EditValue>}
        </RailRow>

        <RailRow label="Owner">
          {editing === 'owner' ? (() => { const f = field('Owner', () => ({ assignedEmployeeId: draft.assignedEmployeeId })); return (
            <span className="inline-flex flex-wrap justify-end items-center gap-2" onKeyDown={f.onKeyDown}>
              <OwnerSelect value={draft.assignedEmployeeId} onChange={id => setDraft({ ...draft, assignedEmployeeId: id })} className={`${inputCls} w-48 bg-surface-card`}
                inheritHint={deal.owner && !deal.assignedEmployeeId ? `Currently ${deal.owner}` : undefined} />
              {f.actions}
            </span>) })() : <EditValue label="Owner" onEdit={() => open('owner')}>{deal.owner || <span className="text-ink-muted font-normal">{records ? 'Assign' : 'Unassigned'}</span>}</EditValue>}
        </RailRow>

        <RailRow label="Source">
          {editing === 'source' ? (() => { const f = field('Source', () => ({ source: draft.source })); return (
            <span className="inline-flex flex-wrap justify-end items-center gap-2" onKeyDown={f.onKeyDown}>
              <select value={draft.source} onChange={e => setDraft({ ...draft, source: e.target.value })} className={`${inputCls} w-40 bg-surface-card`} aria-label="Source" autoFocus>
                <option value="">{deal.source === '' ? 'No source' : '(keep current)'}</option>
                {/* SB-6: a stored legacy value pre-selects for honesty. */}
                {deal.source !== '' && !SOURCE_OPTIONS.some(o => o.value === deal.source) && <option value={deal.source}>{deal.source}</option>}
                {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {f.actions}
            </span>) })() : <EditValue label="Source" onEdit={() => open('source')}>{deal.source ? sourceLabel(deal.source) : <span className="text-ink-muted font-normal">{records ? 'Add a source' : 'Not set'}</span>}</EditValue>}
        </RailRow>

        <RailRow label="Priority">
          {editing === 'priority' ? (() => { const f = field('Priority', () => ({ priority: draft.priority })); return (
            <span className="inline-flex flex-wrap justify-end items-center gap-2" onKeyDown={f.onKeyDown}>
              <select value={draft.priority} onChange={e => setDraft({ ...draft, priority: e.target.value })} className={`${inputCls} w-36 bg-surface-card`} aria-label="Priority" autoFocus>
                <option value="">{deal.priority === '' ? 'No priority' : '(keep current)'}</option>
                {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {f.actions}
            </span>) })() : !may('priority')
            ? <Frozen>{priorityLabel ?? 'Not set'}</Frozen>
            : <EditValue label="Priority" onEdit={() => open('priority')} style={deal.priority === 'high' ? { color: warning.text } : undefined}>
                {priorityLabel ?? <span className="text-ink-muted font-normal">{records ? 'Set a priority' : 'Not set'}</span>}
              </EditValue>}
        </RailRow>

        <RailRow label="Account">
          {editing === 'account' ? (
            <span className="inline-flex flex-wrap justify-end items-center gap-2 w-60">
              <AccountPicker id="deal-row-account" value={deal.accountId ? { accountId: draft.accountId, company: draft.accountName } : linkPick}
                onChange={p => {
                  if (deal.accountId) { setDraft({ ...draft, accountId: p.accountId, accountName: p.company }); if (p.accountId) void save({ accountId: p.accountId, accountName: p.company }, 'Account') }
                  else void linkAccount(p)
                }}
                className={`${inputCls} w-full`} placeholder="Search accounts…" autoFocus />
              <button type="button" className="k-btn-quiet" onClick={close}>Cancel</button>
            </span>
          ) : deal.accountId ? (
            <span className="inline-flex items-center gap-2">
              {onOpenAccount ? (
                <button type="button" onClick={() => onOpenAccount(deal.accountId!)} title="Open account"
                  className="hover:underline inline-flex items-center gap-1" style={{ color: accent.DEFAULT }}>
                  <Building2 size={12} aria-hidden="true" />{deal.company || 'Account'}
                </button>
              ) : <span>{deal.company}</span>}
              {records && <button type="button" className="k-btn-quiet" aria-label="Change account" onClick={() => open('account')}><Pencil size={12} aria-hidden="true" /></button>}
            </span>
          ) : !records ? (
            <span className="text-ink-muted font-normal">Not linked</span>
          ) : (
            <button type="button" className="k-btn-quiet inline-flex items-center gap-1"
              onClick={() => { setLinkPick({ accountId: '', company: deal.company }); setEditing('account') }}>
              <Link2 size={12} aria-hidden="true" /> Link account
            </button>
          )}
        </RailRow>

        {createdText && <RailRow label="Created">{createdText}</RailRow>}

        {/* R8: deal.notes as a Details row — visible and editable. */}
        <RailRow label="Notes" align="start">
          {editing === 'notes' ? (
            <span className="flex flex-col items-end gap-2 w-60" onKeyDown={keys(() => void saveNotes(), close)}>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} aria-label="Deal notes" autoFocus
                className="w-full px-3 py-2 border border-edge-control rounded-control text-13 font-normal text-ink-primary bg-surface-control focus:outline-none focus:border-accent resize-none" />
              {deal.notes !== '' && notes.trim() === '' && <span className="text-12 text-ink-muted font-normal">Notes cannot be emptied yet — type replacement text to save.</span>}
              <EditorActions saving={notesSaving} disabled={notes.trim() === '' || notes === deal.notes} onSave={() => void saveNotes()} onCancel={close} />
            </span>
          ) : (
            <EditValue label="Notes" onEdit={() => { setNotes(deal.notes); setEditing('notes') }}>
              {deal.notes
                ? <span className="block max-w-60 text-13 font-normal text-ink-secondary whitespace-pre-wrap text-left line-clamp-4">{deal.notes}</span>
                : <span className="text-ink-muted font-normal">{records ? 'Add notes' : 'No notes'}</span>}
            </EditValue>
          )}
        </RailRow>

        {deal.issuedInvoiceId && <InvoiceRow invoiceId={deal.issuedInvoiceId} onNavigate={onNavigate} />}
        {deal.lostReason && (
          <RailRow label="Lost reason">
            {lostReasonLabel(deal.lostReason)}
            {deal.lostReasonNote && <span className="text-ink-secondary"> — {deal.lostReasonNote}</span>}
          </RailRow>
        )}
      </div>
    </div>
  )
}
